import { collections } from "@/data/products";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { CTASection } from "@/components/site/CTASection";
import { CheckCircle2 } from "lucide-react";

const advantages = [
  "Measured professionally — no guesswork, no trimming at home",
  "Made to your windows, not the nearest stock size",
  "Hundreds of fabrics and finishes viewed in your own home",
  "Fitted, checked and tidied by experienced fitters",
];

export default function Blinds() {
  return (
    <>
      <SEO
        title="Made-to-Measure Blinds Birmingham | Roller, Roman, Venetian & More"
        description="Explore our full range of made-to-measure blinds in Birmingham: roller, roman, venetian, vertical, day & night, Perfect Fit, motorised, skylight and blackout blinds — measured and fitted for you."
        path="/blinds"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blinds", path: "/blinds" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <Eyebrow>Our Blinds</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              Made-to-Measure Blinds for Every Room
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              A made-to-measure blind simply fits better: no gaps at the edges,
              no compromised proportions, and a finish that looks like it was
              always part of the room. Because it was.
            </p>
            <ul className="mt-6 space-y-2.5">
              {advantages.map((a) => (
                <li key={a} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 mt-0 text-navy-800 shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              {collections.slice(0, 4).map((p) => (
                <div key={p.slug} className="overflow-hidden rounded-2xl aspect-square shadow-lg">
                  <img
                    src={p.image}
                    alt={`Made-to-measure ${p.name.toLowerCase()}`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="blinds-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Choose your style
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Every blind below is made to measure and professionally fitted.
              Not sure which suits your room? Book a free visit and compare
              real samples at home.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {collections.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <ProductCard product={p} className="h-full" eager={i < 3} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Can't decide? Let us bring the samples to you."
        text="Book a free home design visit and compare fabrics, colours and finishes in the rooms they'll live in."
        source="blinds_page_cta"
      />
    </>
  );
}
